import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { me, refresh, type UserPublic } from "@/api/auth-api";
import { keepSessionAlive } from "@/api/client";
import { useOrganisation } from "@/api/organisation-api";
import { IdleWarningDialog } from "@/components/IdleWarningDialog";
import { useIdleTimeout } from "@/hooks/useIdleTimeout";
import { useLogout } from "@/hooks/useLogout";
import { useAuthStore, type AuthUser } from "@/store/auth-store";

// Automatic logoff after this many idle minutes. Env-overridable per
// deployment (a busy shared room may want it shorter); defaults to 15,
// matching the access-token lifetime. Guarded so a garbage value or 0
// falls back to the default rather than logging everyone out instantly.
const IDLE_MINUTES = (() => {
  const raw = Number(import.meta.env.VITE_IDLE_LOGOUT_MINUTES);
  return Number.isFinite(raw) && raw > 0 ? raw : 15;
})();
// The last minute of the idle window is the warning countdown, so the user
// is told before it happens rather than dropped silently.
const IDLE_WARN_MS = 60_000;

// How often an OPEN tab rotates its session in the background.
//
// Must stay comfortably under the server's inactivity window
// (``jwt_refresh_minutes``, 30): that window is the refresh cookie's own
// lifetime and only slides when somebody calls /auth/refresh. The
// candidate screens make no background requests whatsoever -- no
// polling, and the application form saves only while typing -- so
// without this a candidate reading for half an hour is thrown to the login
// screen by their next keystroke.
//
// This does NOT keep an abandoned tab alive: the idle timer above logs
// out at 15 minutes, and the server's absolute cap ends the session at
// 12 hours no matter what. It keeps alive a tab somebody is using.
const KEEPALIVE_MS = 10 * 60_000;

// The store carries a slim identity-only AuthUser; the wire-format
// UserPublic adds license_number, last_login_at, created_at which
// belong to the user-list cache, not the auth context.
function toAuthUser(u: UserPublic): AuthUser {
  return {
    id: u.id,
    username: u.username,
    role: u.role,
    full_name: u.full_name,
    email: u.email,
    active: u.active,
  };
}

type Status = "checking" | "ready" | "unauthorized" | "unreachable";

// Module-level dedup. React StrictMode double-mounts components in
// dev, which would fire two parallel /auth/refresh calls — and refresh
// tokens are SINGLE-USE (security invariant 2): the second call gets
// 401 because the first one already rotated the JTI. Same pattern as
// the 401-retry dedup in api/client.ts.
let bootstrapPromise: Promise<BootstrapOutcome> | null = null;

type BootstrapOutcome = "ok" | "rejected" | "unreachable";

/**
 * Only the server explicitly REFUSING the refresh token means the session
 * is over. Everything else is transient and must not log anybody out.
 *
 * This mirrors the classification in ``api/client.ts`` — and it is here
 * because it was missing here. The interceptor learned this lesson on
 * 2026-07-31 (a backend restart threw every sonographer to the login
 * screen mid-worksheet) and the fix was applied there only, leaving the
 * bare ``catch`` on the STARTUP path, which is the one that runs on every
 * page load.
 *
 * Found on VOXELSAT01 2026-08-06: the third hard refresh inside a minute
 * gets 429 from ``refresh_rate_limit``, the catch-all called that a dead
 * session, and the user was returned to the login screen. Nothing had
 * logged out, which is why no logout appears in the audit trail.
 */
async function bootstrapOnce(): Promise<BootstrapOutcome> {
  try {
    const r = await refresh();
    // Set the token BEFORE calling /me — apiClient reads it from the
    // store at request time and the /me dep needs it.
    useAuthStore.getState().setAccessToken(r.access_token);
    const u = await me();
    useAuthStore.getState().setUser(toAuthUser(u));
    return "ok";
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 401 || status === 403) {
      useAuthStore.getState().clearAuth();
      return "rejected";
    }
    // Session preserved deliberately: a 429, a 502 while the container
    // restarts, or no response at all says nothing about whether the
    // refresh cookie is still good.
    return "unreachable";
  }
}

/**
 * AuthGate — bootstrap the in-memory access token from the HttpOnly
 * refresh cookie on app mount.
 *
 * The access token deliberately does not survive a reload (SEC-AUTH-05:
 * memory-only, no localStorage / sessionStorage). On any fresh tab, the
 * gate exchanges the refresh cookie for a new access token; on failure
 * it redirects to /login. This is also the path Playwright's role
 * fixtures rely on — they mint a fresh refresh cookie per test, and the
 * gate exchanges it for the matching access token.
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const accessToken = useAuthStore((s) => s.accessToken);
  const location = useLocation();
  const logout = useLogout();

  const [status, setStatus] = useState<Status>(
    accessToken ? "ready" : "checking",
  );

  // Idle auto-logoff — armed only while authenticated, so it never runs on
  // the login screen itself. ``logout`` clears the in-memory session and
  // routes to /login; the next login resets the worklist filters.
  const idle = useIdleTimeout(status === "ready", {
    timeoutMs: IDLE_MINUTES * 60_000,
    warnMs: IDLE_WARN_MS,
    onIdle: () => {
      void logout();
    },
  });

  // Background rotation while the tab is open. Armed only once
  // authenticated, so it never fires on the login screen.
  useEffect(() => {
    if (status !== "ready") return undefined;
    const id = window.setInterval(() => {
      void keepSessionAlive();
    }, KEEPALIVE_MS);
    return () => window.clearInterval(id);
  }, [status]);

  useEffect(() => {
    if (accessToken) {
      setStatus("ready");
      return;
    }
    let cancelled = false;
    const pending = (bootstrapPromise ??= bootstrapOnce().finally(() => {
      bootstrapPromise = null;
    }));
    pending.then((outcome) => {
      if (cancelled) return;
      setStatus(
        outcome === "ok"
          ? "ready"
          : outcome === "rejected"
            ? "unauthorized"
            : "unreachable",
      );
    });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  if (status === "checking") {
    return (
      <div
        data-cy="auth-loading"
        className="flex min-h-screen items-center justify-center bg-background text-sm text-foreground-muted"
      >
        Loading…
      </div>
    );
  }
  if (status === "unreachable") {
    // NOT the login screen. The session may well still be valid; sending
    // the user to log in again would be a lie, and on a rate-limit it
    // would also make things worse by spending another attempt.
    return (
      <div
        data-cy="auth-unreachable"
        className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background px-6 text-center"
      >
        <p className="text-sm font-medium text-foreground">
          Cannot reach the server right now.
        </p>
        <p className="max-w-sm text-xs text-foreground-muted">
          Your session has not ended. This is usually a restart or a brief
          hiccup — wait a moment and try again.
        </p>
        <button
          type="button"
          data-cy="auth-retry"
          onClick={() => setStatus("checking")}
          className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-background-card"
        >
          Try again
        </button>
      </div>
    );
  }
  if (status === "unauthorized") {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }
  return (
    <>
      <OrgTimeZoneArmer />
      {children}
      {idle.warning ? (
        <IdleWarningDialog
          secondsLeft={idle.secondsLeft}
          onStay={idle.stayActive}
          onLogoutNow={() => void logout()}
        />
      ) : null}
    </>
  );
}

/**
 * Loads the organisation so its timezone reaches the date formatters.
 *
 * Mounted here, inside the authenticated shell, because EVERY page must
 * render timestamps on the clinic clock — including Admin (the audit
 * log), Reports and Conflicts, none of which otherwise read the
 * organisation. Relying on each page to load it is how a surface ends
 * up quietly rendering the browser's clock instead.
 *
 * Renders nothing; ``useOrganisation`` performs the side effect and the
 * query is shared/cached with every other consumer.
 */
function OrgTimeZoneArmer() {
  useOrganisation();
  return null;
}
